import { toHex } from "viem";
import { mnemonicToAccount } from "viem/accounts";

import type { Address, Hex } from "viem";

/** Anvil-compatible HD mnemonic (deterministic, well-known). */
export const MNEMONIC =
  "test test test test test test test test test test test junk" as const;

/** Default ETH balance for prefunded accounts: 10,000 ETH. */
export const DEFAULT_BALANCE = 10_000n * 10n ** 18n;

/** A prefunded test account with address, private key, and label. */
export interface PrefundedAccount {
  address: Address;
  privateKey: Hex;
  label: string;
  balance: bigint;
}

/** Labels for the first 20 accounts. */
const LABELS = [
  "deployer",
  "alice",
  "bob",
  "carol",
  "dave",
  "eve",
  "frank",
  "grace",
  "heidi",
  "ivan",
  "account-10",
  "account-11",
  "account-12",
  "account-13",
  "account-14",
  "account-15",
  "account-16",
  "account-17",
  "account-18",
  "account-19",
] as const;

/**
 * Derive a single HD account from the mnemonic at the given index.
 * Uses BIP-44 path: m/44'/60'/0'/0/{index}
 */
function deriveAccount(index: number): { address: Address; privateKey: Hex } {
  const hdAccount = mnemonicToAccount(MNEMONIC, { addressIndex: index });
  const rawKey = hdAccount.getHdKey().privateKey;
  if (!rawKey) {
    throw new Error(`Failed to derive private key for index ${String(index)}`);
  }
  return {
    address: hdAccount.address,
    privateKey: toHex(rawKey),
  };
}

/** 20 deterministic HD accounts matching Anvil's default set + 10 extra. */
export const PREFUNDED_ACCOUNTS: readonly PrefundedAccount[] = Array.from(
  { length: 20 },
  (_, i) => {
    const { address, privateKey } = deriveAccount(i);
    return {
      address,
      privateKey,
      label: LABELS[i] ?? `account-${String(i)}`,
      balance: DEFAULT_BALANCE,
    };
  },
);

/** Get the deployer account (index 0). */
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const DEPLOYER = PREFUNDED_ACCOUNTS[0]!;

/** Get an account by index or label. */
export function getAccount(indexOrLabel: number | string): PrefundedAccount {
  if (typeof indexOrLabel === "number") {
    const account = PREFUNDED_ACCOUNTS[indexOrLabel];
    if (!account) {
      throw new Error(
        `Account index ${String(indexOrLabel)} out of range (0-19)`,
      );
    }
    return account;
  }
  const account = PREFUNDED_ACCOUNTS.find((a) => a.label === indexOrLabel);
  if (!account) {
    throw new Error(`Account with label "${indexOrLabel}" not found`);
  }
  return account;
}
